import { Api, ws } from 'lib';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { FANWAVE_STATES, when } from '../labState';
import { Btn, controlClass, FeatureCard, Pill, ProgressBar, TeamColumn } from '../ui';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const small = 'h-7 text-[12px]';
const WAVE_TONES = { active: 'live', scheduled: 'info', completed: 'done', cancelled: 'idle' };

const GroupLabel = ({ children }) => (
  <span className="w-full text-[11px] font-semibold uppercase tracking-wide text-[#94A3B8]">
    {children}
  </span>
);

const FanWaveColumn = ({ side, teamId, teamName, game, data, act, busy }) => {
  const [target, setTarget] = useState('100');
  const [tapping, setTapping] = useState(false);
  const status = data?.status;
  const waves = (data?.waves || []).filter((w) => String(w.gameId) === String(game._id));
  const running = waves.find((w) => ['active', 'scheduled'].includes(w.status));
  const meaning = FANWAVE_STATES[status?.state] || FANWAVE_STATES.no_fanwave;
  const open = status?.state === 'ready_to_play';
  const quota = status?.rewards?.tapQuota?.enabled ? status.rewards.tapQuota.maxTapsPerUser : null;

  const timing =
    status?.state === 'coming_up' && status.opensAt
      ? `Opens ${when(status.opensAt)}`
      : open && status.closesAt
        ? `Closes ${when(status.closesAt)}`
        : null;

  const createWave = () =>
    act('Create FanWave', () =>
      Api.post(
        '/admin/fanwave/create',
        { teamId, mode: 'game_day', gameId: game._id, target: Number(target) },
        null,
        { silent: true }
      )
    );
  const cancelWave = () =>
    act('Cancel FanWave', () =>
      Api.post('/admin/fanwave/cancel', { teamId }, null, { silent: true })
    );

  async function tap(count) {
    setTapping(true);
    const key = status?.waveId || status?.gameId || game._id;
    ws.emit('join_fanwave', { teamId, gameId: key });
    let sent = 0;
    for (let i = 0; i < count; i++) {
      if (!ws.emit('fanwave_click', { teamId, gameId: key })) break;
      sent++;
      // Yield now and then so a burst of a hundred does not freeze the page.
      if (i % 25 === 24) await new Promise((r) => setTimeout(r, 0));
    }
    setTapping(false);
    await act(`Send ${count} tap${count === 1 ? '' : 's'}`, async () =>
      sent ? true : { err: 'the CMS is not connected to the live socket, so no tap was sent.' }
    );
  }

  return (
    <TeamColumn
      side={side}
      teamName={teamName}
      status={{ tone: meaning.tone, label: `${meaning.label} — ${meaning.detail}`, detail: timing }}
      actions={
        <>
          <GroupLabel>Set up</GroupLabel>
          <label className="flex items-center gap-1.5 text-[12px] text-[#475569]">
            Target
            <input
              aria-label={`${teamName} FanWave tap target`}
              inputMode="numeric"
              className={`${controlClass} w-20`}
              value={target}
              onChange={(e) => setTarget(e.target.value.replace(/[^0-9]/g, ''))}
            />
          </label>
          <Btn className={small} disabled={busy || Number(target) < 10} onClick={createWave}>
            + Create wave (half-time → full time)
          </Btn>
          <Btn className={small} variant="danger" disabled={busy || !running} onClick={cancelWave}>
            Cancel wave
          </Btn>
          <div className="w-full h-1" />
          <GroupLabel>Simulate taps (as you)</GroupLabel>
          {[1, 10, 100].map((n) => (
            <Btn key={n} className={small} disabled={tapping || !open} onClick={() => tap(n)}>
              Tap ×{n}
            </Btn>
          ))}
          {!open && (
            <span className="text-[12px] text-[#94A3B8]">
              Taps only count while the wave is open.
            </span>
          )}
          <Link
            className="ml-auto text-[12px] text-[#4F46E5] hover:underline"
            to={`/admin/fanwave?team=${teamId}&tab=timeline`}
          >
            Open in FanWave →
          </Link>
        </>
      }
    >
      {status && status.state !== 'no_fanwave' && (
        <>
          <ProgressBar value={status.totalHits} max={status.tapTarget} />
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-[#64748B]">
            <span>{fmt(status.totalParticipants)} fans tapping</span>
            <span>
              Your taps: {fmt(status.userTaps)}
              {quota ? ` of ${fmt(quota)} allowed` : ''}
            </span>
          </div>
        </>
      )}
      {waves.length > 0 ? (
        waves.map((w, i) => (
          <div
            key={w.waveId || i}
            className="flex items-center justify-between gap-2 rounded-lg border border-[#E2E8F0] px-3 py-2 text-[12px]"
          >
            <span className="text-[#475569]">
              Wave on this game · target {fmt(w.tapTarget)} · {fmt(w.totalHits)} taps
            </span>
            <Pill tone={WAVE_TONES[w.status] || 'idle'}>{w.status}</Pill>
          </div>
        ))
      ) : (
        <p className="text-[12px] text-[#94A3B8]">No wave on this game yet.</p>
      )}
    </TeamColumn>
  );
};

const FanWaveCard = ({ sides, game, fanwave, act, busy }) => (
  <FeatureCard
    title="FanWave"
    summary="A Game Day wave opens at half-time and stays open until full time plus a short window. Every tap adds to one shared target. Status and taps are what you, signed in as this admin, see."
  >
    {sides.map(({ side, teamId, teamName }) => (
      <FanWaveColumn
        key={side}
        side={side}
        teamId={teamId}
        teamName={teamName}
        game={game}
        data={fanwave?.[side]}
        act={act}
        busy={busy}
      />
    ))}
  </FeatureCard>
);

export default FanWaveCard;
